import { faAngleDown, faAngleUp } from "@fortawesome/pro-regular-svg-icons";
import React from "react";

import { CheckboxWithCount } from "@/mk2/apps/strollers/containers/StrollersSearch/CheckboxWithCount";
import { FacetHelp } from "@/mk2/apps/strollers/containers/StrollersSearch/FacetHelp";
import { FacetOption } from "@/mk2/apps/strollers/containers/StrollersSearch/StrollerSearchForm";
import { ClickableField } from "@/mk2/components/forms/ClickableField";
import useEventCallback from "@/mk2/hooks/useEventCallback";
import { ContentItemEntity } from "@/mk2/schemas";

import styles from "./FacetField.mscss";

interface OwnProps {
    name: string;
    label: string;
    opened: boolean;
    options: FacetOption[];
    selected: string[];
    counts?: Record<string, number>;
    help?: ContentItemEntity;

    additionalValue?: React.ReactNode;
    children?: React.ReactNode;

    onToggle(name: string);
    onChange(name: string, value: string);
}

type Props = OwnProps;

export const FacetField: React.FunctionComponent<Props> = ({
    name,
    label,
    opened,
    options,
    selected,
    additionalValue,
    children,
    onToggle,
    onChange,
    counts,
    help,
}) => {
    const onToggleCallback = useEventCallback(() => {
        onToggle(name);
    }, [name, onToggle]);

    return (
        <>
            <ClickableField
                name={name}
                label={label}
                onClick={onToggleCallback}
                minimizedLabel={!!additionalValue}
                ValueComponent={({ value }) => {
                    const selectedOptions = value
                        .map((v) => options.find((o) => o.value === v))
                        .filter(Boolean);
                    return (
                        <span className={styles.FacetField__value}>
                            {selectedOptions.map((o, i) => (
                                <React.Fragment key={o.value}>
                                    {o.label}
                                    {i < selectedOptions.length - 1 && ", "}
                                </React.Fragment>
                            ))}
                            {additionalValue && (
                                <>
                                    {selectedOptions.length > 0 && ", "}
                                    {additionalValue}
                                </>
                            )}
                        </span>
                    );
                }}
                arrowIcon={opened ? faAngleUp : faAngleDown}
            />
            {opened && (
                <div className={styles.FacetField__options}>
                    {options.map((opt) => (
                        <CheckboxWithCount
                            key={opt.value}
                            name={name}
                            label={opt.label}
                            value={opt.value}
                            checked={selected.includes(opt.value)}
                            onChange={onChange}
                            count={counts?.[opt.value]}
                        />
                    ))}

                    {children}

                    {help && <FacetHelp help={help} />}
                </div>
            )}
        </>
    );
};

FacetField.displayName = "FacetField";
